import React, { useState } from 'react';
import { Package, Minus, Plus, Info } from 'lucide-react';

const ProductUnitSelector = ({ product, onAddToCart, onClose }) => {
  const [units, setUnits] = useState(product.package_info?.min_units || 1);
  const [isPartial, setIsPartial] = useState(false);

  const maxUnits =
    product.package_info?.total_units ??
    product.total_units_per_package ??
    1;

  const minUnits =
    product.package_info?.min_units ??
    product.min_sale_units ??
    1;

  const pricePerUnit = Number(
    product.package_info?.price_per_unit ??
    product.price_per_unit ??
    product.selling_price
  );

  const fullPackagePrice = Number(
    product.package_info?.full_package_price ??
    product.selling_price
  );

  // Newer DB uses allow_partial_sale, older UI used package_info.can_split
  const canSplit =
    product.package_info?.can_split ??
    product.allow_partial_sale ??
    false;


  const totalPrice = units * pricePerUnit;

  const handleUnitChange = (delta) => {
    const newUnits = units + delta;
    if (newUnits >= minUnits && newUnits <= maxUnits) {
      setUnits(newUnits);
      setIsPartial(newUnits < maxUnits);
    }
  };

  const handleQuickSelect = (unitCount) => {
    setUnits(unitCount);
    setIsPartial(unitCount < maxUnits);
  };

  const handleAddToCart = () => {
    onAddToCart({
      ...product,
      units_requested: units,
      is_partial_sale: isPartial,
      display_price: totalPrice,
      display_label: isPartial 
        ? `${units} ${product.unit_type || 'tablets'} of ${product.name}`
        : `Full package: ${product.name}`,
      package_info: {
        ...product.package_info,
        units_selected: units,
        total_price: totalPrice,
        is_partial: isPartial
      }
    });
    onClose();
  };

  const quickSelects = [];
  if (canSplit) {
    // Generate quick selection buttons
    if (maxUnits >= 30) quickSelects.push(Math.floor(maxUnits / 2)); // Half
    if (maxUnits >= 14) quickSelects.push(Math.floor(maxUnits / 4)); // Quarter
    if (maxUnits >= 7) quickSelects.push(Math.floor(maxUnits / 7)); // Week
  }
  quickSelects.push(maxUnits); // Full package

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-bold">{product.name}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">✕</button>
        </div>

        {/* Product Info */}
        <div className="bg-green-50 rounded-lg p-4 mb-6">
          <div className="flex items-center space-x-2 mb-2">
            <Package size={20} className="text-green-600" />
            <span className="font-medium">Package Details</span>
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span>Total Units:</span>
              <span className="font-medium">{maxUnits} {product.unit_type || 'tablets'}</span>
            </div>
            <div className="flex justify-between">
              <span>Price per unit:</span>
              <span className="font-medium">KES {pricePerUnit.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span>Full package price:</span>
              <span className="font-medium">KES {fullPackagePrice.toLocaleString()}</span>
            </div>
          </div>
        </div>

        {/* Unit Selector */}
        {canSplit ? (
          <>
            <div className="mb-6">
              <label className="block text-sm font-medium mb-3">
                Select number of {product.unit_type || 'tablets'}:
              </label>
              
              {/* Quick Selection Buttons */}
              <div className="flex gap-2 mb-4">
                {quickSelects.map((qty) => (
                  <button
                    key={qty}
                    onClick={() => handleQuickSelect(qty)}
                    className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                      units === qty
                        ? 'bg-green-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {qty === maxUnits ? 'Full' : `${qty} units`}
                  </button>
                ))}
              </div>

              {/* Manual Quantity Selector */}
              <div className="flex items-center justify-center space-x-4">
                <button
                  onClick={() => handleUnitChange(-1)}
                  disabled={units <= minUnits}
                  className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 disabled:opacity-50"
                >
                  <Minus size={20} />
                </button>
                
                <div className="text-center">
                  <input
                    type="number"
                    value={units}
                    onChange={(e) => {
                      const val = parseInt(e.target.value);
                      if (val >= minUnits && val <= maxUnits) {
                        setUnits(val);
                        setIsPartial(val < maxUnits);
                      }
                    }}
                    className="w-20 text-center text-2xl font-bold border rounded-lg py-2"
                    min={minUnits}
                    max={maxUnits}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {product.unit_type || 'tablets'}
                  </p>
                </div>

                <button
                  onClick={() => handleUnitChange(1)}
                  disabled={units >= maxUnits}
                  className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 disabled:opacity-50"
                >
                  <Plus size={20} />
                </button>
              </div>
            </div>

            {/* Price Breakdown */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Price per unit:</span>
                  <span>KES {pricePerUnit.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Quantity:</span>
                  <span>{units} {product.unit_type || 'tablets'}</span>
                </div>
                {isPartial && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded p-2 mt-2">
                    <div className="flex items-center space-x-2">
                      <Info size={16} className="text-yellow-600" />
                      <p className="text-xs text-yellow-700">
                        This is a partial package. {maxUnits - units} {product.unit_type || 'tablets'} will remain in stock.
                      </p>
                    </div>
                  </div>
                )}
                <div className="border-t pt-2 mt-2">
                  <div className="flex justify-between font-bold text-lg">
                    <span>Total:</span>
                    <span className="text-green-600">KES {totalPrice.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-6">
            <Package size={48} className="mx-auto text-gray-400 mb-3" />
            <p className="text-gray-600">Full package only</p>
            <p className="text-2xl font-bold text-green-600 mt-2">
              KES {fullPackagePrice.toLocaleString()}
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex space-x-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleAddToCart}
            className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            Add to Cart - KES {totalPrice.toLocaleString()}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductUnitSelector;
